import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
} from 'react'
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom'
import type { TourDemoType } from '../tour-styles/types'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    startGuidedTour,
    type GuidedTour,
    type GuidedTourStep,
} from '../tour-styles'

export interface QuickHelpOption {
    id: string
    label: string
    description: string
    /** CSS selector (data-tour) OR button label text */
    selector?: string
    buttonLabel?: string
    tourTitle: string
    tourDescription: string
    /** Optional: Navigate to this path before showing hint */
    navigateTo?: string
    /** Optional: Demo animation type from tour-styles.ts (for single hint) */
    demo?: TourDemoType
    /**
     * Optional: Multi-step tour steps (uses tour-styles.ts). A step may live on
     * another page: declare it with `path` / `to` (see `GuidedTourStep`) and the
     * runtime navigates, waits for the target and carries on there.
     */
    tourSteps?: GuidedTourStep[]
    /** Optional: runs when the multi-step tour ends or is closed (undo `beforeShow`). */
    onTourEnd?: () => void
}

interface QuickHelpContextType {
    /** Register quick help options for current page */
    registerOptions: (options: QuickHelpOption[]) => void
    /** Unregister options */
    unregisterOptions: () => void
    /** Check if options exist */
    hasOptions: boolean
    /** Options registered by the current page (top of the stack) */
    options: QuickHelpOption[]
    /** Run one option (hint, multi-step tour, or navigate-then-hint) */
    runOption: (option: QuickHelpOption) => void
}

const QuickHelpContext = createContext<QuickHelpContextType | null>(null)

/**
 * Find element by selector or button label
 */
const findElement = (option: QuickHelpOption): HTMLElement | null => {
    // Try selector first
    if (option.selector) {
        const el = document.querySelector(option.selector) as HTMLElement
        if (el) return el
    }

    // Try button label
    if (option.buttonLabel) {
        const buttons = document.querySelectorAll('button')
        for (const button of buttons) {
            if (button.textContent?.trim() === option.buttonLabel) {
                return button as HTMLElement
            }
        }
    }

    return null
}

export const QuickHelpProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const navigationType = useNavigationType()
    const activeTourRef = useRef<GuidedTour | null>(null)
    const navigateRef = useRef(navigate)
    navigateRef.current = navigate
    // Stack-based options to support nested components (Modal inside Page)
    const [optionsStack, setOptionsStack] = useState<QuickHelpOption[][]>([])
    const optionsRef = useRef<QuickHelpOption[]>([])
    const pendingHintRef = useRef<QuickHelpOption | null>(null)

    // Get current options from top of stack
    const options = optionsStack[optionsStack.length - 1] || []

    // Keep ref in sync
    useEffect(() => {
        optionsRef.current = options
    }, [options])

    const registerOptions = useCallback((newOptions: QuickHelpOption[]) => {
        // Push new options onto stack
        setOptionsStack((prev) => [...prev, newOptions])
    }, [])

    const unregisterOptions = useCallback(() => {
        // Pop options from stack
        setOptionsStack((prev) => prev.slice(0, -1))
    }, [])

    /**
     * Show single-step hint using tour-styles.ts (rich styling with keyboard hints)
     */
    const showHintPopover = useCallback((option: QuickHelpOption) => {
        const element = findElement(option)

        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        // If element found, highlight and scroll to it
        if (element) {
            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        setTimeout(
            () => {
                const popover = createTourPopover({
                    title: option.tourTitle,
                    description: option.tourDescription,
                    currentStep: 1,
                    totalSteps: 1,
                    onNext: () => {
                        cleanupTourElements()
                    },
                    onClose: () => {
                        cleanupTourElements()
                    },
                    isLastStep: true,
                    demo: option.demo,
                })

                document.body.appendChild(popover)

                // Position popover: center of screen if no element, or near element
                if (element) {
                    positionPopover(popover, element)
                } else {
                    // Center popover on screen for info-only hints
                    popover.style.position = 'fixed'
                    popover.style.top = '50%'
                    popover.style.left = '50%'
                    popover.style.transform = 'translate(-50%, -50%)'
                }
            },
            element ? 300 : 100
        )
    }, [])

    /**
     * Show multi-step tour (tour-styles `startGuidedTour`). One at a time: a new
     * one ends the previous. The tour ends on Escape / close, on the last step,
     * on browser Back (see the effect below) and when this provider unmounts.
     */
    const showMultiStepTour = useCallback(
        (steps: QuickHelpOption['tourSteps'], onTourEnd?: () => void) => {
            if (!steps || steps.length === 0) return
            activeTourRef.current?.stop()
            const tour = startGuidedTour({
                steps,
                navigate: (url) => navigateRef.current(url),
                getUrl: () => window.location.pathname + window.location.search,
                onEnd: () => {
                    if (activeTourRef.current === tour)
                        activeTourRef.current = null
                    onTourEnd?.()
                },
            })
            activeTourRef.current = tour
        },
        []
    )

    const handleSelectOption = useCallback(
        (option: QuickHelpOption) => {
            // If tourSteps is provided, start multi-step tour with tour-styles.ts
            if (option.tourSteps && option.tourSteps.length > 0) {
                // Small delay to let the help menu close first
                setTimeout(() => {
                    showMultiStepTour(option.tourSteps, option.onTourEnd)
                }, 100)
                return
            }

            // If navigateTo is set and we're not already there, navigate first
            if (
                option.navigateTo &&
                !location.pathname.startsWith(option.navigateTo)
            ) {
                pendingHintRef.current = option
                navigate(option.navigateTo)
                return
            }

            // Show single-step hint using tour-styles.ts
            showHintPopover(option)
        },
        [location.pathname, navigate, showHintPopover, showMultiStepTour]
    )

    // Show pending hint after navigation
    useEffect(() => {
        if (
            pendingHintRef.current &&
            location.pathname.startsWith(
                pendingHintRef.current.navigateTo || ''
            )
        ) {
            // Wait for page to render
            const timer = setTimeout(() => {
                if (pendingHintRef.current) {
                    showHintPopover(pendingHintRef.current)
                    pendingHintRef.current = null
                }
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [location.pathname, showHintPopover])

    // Browser Back / Forward during a tour ends it (a step's own navigation is
    // a PUSH, never a POP).
    useEffect(() => {
        if (navigationType === 'POP') activeTourRef.current?.notifyPop()
    }, [location.key, navigationType])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            activeTourRef.current?.stop()
            cleanupTourElements()
        }
    }, [])

    return (
        <QuickHelpContext.Provider
            value={{
                registerOptions,
                unregisterOptions,
                hasOptions: options.length > 0,
                options,
                runOption: handleSelectOption,
            }}
        >
            {children}
        </QuickHelpContext.Provider>
    )
}

/**
 * Hook to register quick help options for current page
 */
export const useQuickHelp = (options: QuickHelpOption[]) => {
    const context = useContext(QuickHelpContext)
    if (!context) {
        throw new Error('useQuickHelp must be used within QuickHelpProvider')
    }

    const { registerOptions, unregisterOptions } = context

    // Track if we actually registered to avoid unbalanced push/pop
    const didRegisterRef = useRef(false)

    useEffect(() => {
        if (options.length > 0) {
            registerOptions(options)
            didRegisterRef.current = true
        } else {
            didRegisterRef.current = false
        }
        return () => {
            // Only unregister if we actually registered
            if (didRegisterRef.current) {
                unregisterOptions()
                didRegisterRef.current = false
            }
        }
    }, [options, registerOptions, unregisterOptions])
}

/**
 * Hook to access quick help context directly
 */
export const useQuickHelpContext = () => {
    const context = useContext(QuickHelpContext)
    if (!context) {
        throw new Error(
            'useQuickHelpContext must be used within QuickHelpProvider'
        )
    }
    return context
}
